import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import ReactSelect from "components/base/ReactSelect";
import { EmpresaQueryType, EmpresaType } from "data/empresa";
import { EditarProdutoDtoType, CriarProdutoDtoType } from "data/produto";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { usePermissionMutation } from "hooks/usePermissionQuery";
import { useEmpresas } from "providers/EmpresaProvider";

const fetchRegrasFiscais = async () => {
  const { data } = await axios.get("/api/regraFiscal", {
    params: { pageIndex: 0, pageSize: 9999999 },
  });

  return data.itens;
};

const RegraFiscalEmpresaFormCard = ({ className }: { className?: string }) => {
  const { empresas } = useEmpresas();
  const [isInitialized, setIsInitialized] = useState(false);

  const regrasFiscaisQuery = useQuery({
    queryKey: ["regrasFiscais"],
    queryFn: fetchRegrasFiscais,
  });

  const { control, watch, setValue, getValues } = useFormContext<EditarProdutoDtoType | CriarProdutoDtoType>();

  const codigo = watch("codigo");
  const descricao = watch("descricao");
  const ean = watch("codigosDeBarras");

  const mutationOptions = usePermissionMutation(
    "Você não possui permissão para sanear este produto"
  );

  const sanearMutation = useMutation({
    ...mutationOptions,
    mutationFn: () => {
      return axios.get("/api/produto/sanear", {
        params: { codigo, descricao, ean },
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });
    },
  });

  const { fields, replace } = useFieldArray({
    control,
    name: "empresas",
    rules: { required: true },
  });

  // Verificar se todos os dados necessários estão carregados
  const isDataReady = useMemo(() => {
    return !!(empresas && empresas.length > 0 && regrasFiscaisQuery.data);
  }, [empresas, regrasFiscaisQuery.data]);

  // Memoizar as opções das regras fiscais
  const regrasFiscaisOptions = useMemo(() => {
    if (!regrasFiscaisQuery.data) return [];
    
    return regrasFiscaisQuery.data.map((regraFiscal: any) => ({
      label: `${regraFiscal.codigo} - ${regraFiscal.descricao}`,
      value: regraFiscal.id,
    }));
  }, [regrasFiscaisQuery.data]);

  // Inicialização única quando todos os dados estão prontos
  useEffect(() => {
    if (!isDataReady || isInitialized) return;

    console.log('Inicializando formulário com empresas:', empresas);
    
    // Pegar valores atuais do formulário (se existirem)
    const currentValues = getValues("empresas") || [];
    console.log('Valores atuais do formulário:', currentValues);

    // Criar array de empresas preservando valores existentes
    const empresasFormData = empresas.map((empresa: EmpresaType, index: number) => {
      const existingData = currentValues[index];
      
      return {
        empresaId: empresa.id,
        regraFiscalId: existingData?.regraFiscalId || undefined,
        estoqueMinimo: existingData?.estoqueMinimo || 0,
      };
    });

    console.log('Dados do formulário a serem definidos:', empresasFormData);

    replace(empresasFormData);
    setIsInitialized(true);
  }, [isDataReady, isInitialized, empresas, getValues, replace]);

  const handleSanear = async () => {
    try {
      const response = await sanearMutation.mutateAsync(undefined);
      await regrasFiscaisQuery.refetch();

      if (response?.data?.data && Array.isArray(response.data.data)) {
        response.data.data.forEach((empresaFiscal: any) => {
          const empresaIndex = fields.findIndex(
            (field) => field.empresaId === empresaFiscal.empresaId
          );

          if (empresaIndex !== -1) {
            setValue(
              `empresas.${empresaIndex}.regraFiscalId`,
              empresaFiscal.regraFiscalId,
              { shouldValidate: true, shouldDirty: true }
            );
          }
        });
      }
    } catch (error) {
      console.error('Erro ao sanear produto:', error);
    }
  };

  // Loading state
  if (!isDataReady) {
    return (
      <Card className={className}>
        <Card.Body>
          <h4 className="mb-4">Regras Fiscais</h4>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2">
              {!empresas ? "Carregando empresas..." : 
               regrasFiscaisQuery.isLoading ? "Carregando regras fiscais..." : "Inicializando..."}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // Não renderizar se não estiver inicializado ou não tiver fields
  if (!isInitialized) {
    return (
      <Card className={className}>
        <Card.Body>
          <h4 className="mb-4">Regras Fiscais</h4>
          <div className="text-center">Preparando formulário...</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <Card.Body>
        <h4 className="mb-4">Regras Fiscais</h4>

        <Row className="gx-3 gy-4 mb-3">
          {fields.map((field, index) => {
            const empresa = empresas.find((emp: EmpresaType) => emp.id === field.empresaId);
            if (!empresa) return null;

            const regraFiscalId = watch(`empresas.${index}.regraFiscalId`);
            const selectedOption = regrasFiscaisOptions.find((option: any) => option.value === regraFiscalId);

            console.log(`Empresa ${empresa.nomeFantasia}, regraFiscalId:`, regraFiscalId, 'selectedOption:', selectedOption);

            return (
              <Col key={field.id} xs={12} sm={6} xl={12}>
                <div className="border-bottom border-translucent border-dashed border-sm-0 border-bottom-xl pb-4">
                  <div className="d-flex gap-2 mb-2">
                    <h5 className="mb-0 text-body-highlight">
                      {empresa.nomeFantasia}
                    </h5>
                    <Link className="fw-bold fs-9" to="#!">
                      Visualizar
                    </Link>
                  </div>
                  <ReactSelect
                    key={`select-${field.id}-${regraFiscalId}`} // Force re-render com key
                    options={regrasFiscaisOptions}
                    onChange={(selectedOption: any) => {
                      const newValue = selectedOption?.value || undefined;
                      console.log('Selecionando:', newValue, 'para empresa:', empresa.nomeFantasia);
                      setValue(
                        `empresas.${index}.regraFiscalId`,
                        newValue,
                        { shouldValidate: true, shouldDirty: true }
                      );
                    }}
                    value={selectedOption || null}
                    placeholder="Selecione uma regra fiscal"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </div>
              </Col>
            );
          })}
        </Row>
        
        <Button
          variant="phoenix-primary"
          className="w-100"
          onClick={handleSanear}
          loading={sanearMutation.isPending || regrasFiscaisQuery.isFetching}
          loadingPosition="start"
          disabled={!codigo || !descricao}
        >
          Sanear
        </Button>
      </Card.Body>
    </Card>
  );
};

export default RegraFiscalEmpresaFormCard;